import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCheck, Inbox, SearchX } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Kbd } from '@/components/ui/kbd';
import { Label } from '@/components/ui/label';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { AdminPage, ConfirmActionDialog, EmptyState, PillTabs, SearchField, SectionCard, TablePagination, formatNumber } from '../../../components/design-system/admin';
import { NETWORK_BULK_MAX, networkErrorMessage, useNetworkAdminApi } from '../../../common/hooks/use-network-admin-api.hook';
import { NETWORK_LINK_METHODS, NETWORK_LINK_STATUSES, type BrandRow, type BulkLinkResult, type LinkQuery, type LinkRow, type RetailerSummary } from '../../../common/hooks/network-admin.types';
import type { NetworkLinkMethod, NetworkLinkStatus } from '../../../common/models/network.model';
import { ReviewCard } from '../../../modules/superadmin/network/ReviewCard';
import { ErrorNote, methodLabel } from '../../../modules/superadmin/network/network-parts';
import {
    ReviewFilters,
    Thresholds,
    chunk,
    nextOpenIndex,
    percentToScore,
    readReviewFilters,
    reviewKeyAction,
    scoreToPercent,
    visualThresholds,
    vocabKey,
    writeReviewFilters,
} from '../../../modules/superadmin/network/network-helpers';

const PAGE_SIZE = 20;
/** Ids fetched per request when collecting a bulk selection, and the most a bulk run takes. */
const BULK_FETCH = 200;
const BULK_CAP = 5000;

interface Decision {
    id: string;
    from: NetworkLinkStatus;
    to: NetworkLinkStatus;
    /** Open suggestions for the same retailer product, rejected by the API when this one was confirmed. */
    alsoRejected: string[];
}

const dialogOpen = () => typeof document !== 'undefined' && !!document.querySelector('[role="dialog"], [role="alertdialog"]');

/**
 * The curation queue. Each card puts a retailer's product beside the network
 * product the matcher thinks it is, with every signal it used; a person
 * confirms or rejects, mostly from the keyboard. Decisions show at once and
 * are rolled back (with the API's reason) when the API refuses them.
 *
 * Filters live in the URL, so a link from the overview ("12 suggested for
 * Ceramiche Aurora") opens the queue already narrowed.
 */
export function NetworkReviewPage() {
    const { t } = useTranslation();
    const api = useNetworkAdminApi();
    const [params, setParams] = useSearchParams();

    const filters = readReviewFilters(params);
    const { status, method, vendorId, brandId, minScore, q } = filters;
    /** Zero-based, for the pagination footer; the URL and the API count from 1. */
    const page = filters.page - 1;
    const minPct = scoreToPercent(minScore);

    const [minDraft, setMinDraft] = useState(minPct);
    const [search, setSearch] = useState(q);
    const [reloadKey, setReloadKey] = useState(0);
    const [items, setItems] = useState<LinkRow[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [focus, setFocus] = useState(0);
    const [pending, setPending] = useState<string[]>([]);
    const [history, setHistory] = useState<Decision[]>([]);
    const [decisionError, setDecisionError] = useState<string | null>(null);

    const [retailers, setRetailers] = useState<RetailerSummary[]>([]);
    const [brands, setBrands] = useState<BrandRow[]>([]);
    const [thresholds, setThresholds] = useState<Thresholds>(visualThresholds(null));

    const [bulkPct, setBulkPct] = useState('90');
    const [bulkIds, setBulkIds] = useState<string[] | null>(null);
    const [bulkCollecting, setBulkCollecting] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkResult, setBulkResult] = useState<(BulkLinkResult & { requested: number }) | null>(null);
    const [bulkError, setBulkError] = useState<string | null>(null);

    const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const seq = useRef(0);
    // Links with a call in flight, updated synchronously: a key held down must not send twice.
    const inflight = useRef(new Set<string>());
    // The keyboard handler reads the latest state through this ref instead of re-binding on every change.
    const live = useRef({ items, focus, pending, history, status });
    live.current = { items, focus, pending, history, status };

    /** Every filter lives in the URL; changing one goes back to the first page. */
    const setFilters = (patch: Partial<ReviewFilters>) => {
        setParams(writeReviewFilters({ ...readReviewFilters(params), page: 1, ...patch }), { replace: true });
    };
    const setPage = (p: number) => setFilters({ page: p + 1 });

    useEffect(() => setMinDraft(minPct), [minPct]);
    useEffect(() => setSearch(q), [q]);
    // Typing a score or a search narrows the queue once the person pauses.
    useEffect(() => {
        if (percentToScore(minDraft) === minScore) return undefined;
        const id = window.setTimeout(() => setFilters({ minScore: percentToScore(minDraft) }), 400);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [minDraft]);
    useEffect(() => {
        if (search.trim() === q) return undefined;
        const id = window.setTimeout(() => setFilters({ q: search.trim() }), 300);
        return () => window.clearTimeout(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        api.retailers().then(setRetailers).catch(() => setRetailers([]));
        api.brands().then(setBrands).catch(() => setBrands([]));
        api.stats()
            .then((s) => setThresholds(visualThresholds(s?.surfaceId?.thresholds)))
            .catch(() => undefined);
    }, [api]);

    const query: LinkQuery = useMemo(
        () => ({
            status,
            method: method || undefined,
            vendorId: vendorId || undefined,
            brandId: brandId || undefined,
            minScore,
            q: q || undefined,
            sort: 'score_desc',
        }),
        [status, method, vendorId, brandId, minScore, q],
    );

    const load = useCallback(async () => {
        const mine = ++seq.current;
        setLoading(true);
        setLoadError(null);
        try {
            const res = await api.links({ ...query, page: page + 1, limit: PAGE_SIZE });
            if (mine !== seq.current) return;
            setItems(res.items);
            setTotal(res.total);
            setFocus(0);
            setHistory([]);
            setDecisionError(null);
        } catch (e) {
            if (mine !== seq.current) return;
            setItems([]);
            setTotal(0);
            setLoadError(networkErrorMessage(e) || t('ADMIN_SHELL.COMMON.LOAD_FAILED'));
        } finally {
            if (mine === seq.current) setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, query, page]);

    useEffect(() => {
        load();
    }, [load, reloadKey]);

    // Keep the current card on screen as J/K move through the queue.
    useEffect(() => {
        const item = items[focus];
        const el = item ? cardRefs.current[item._id] : null;
        if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [focus, items]);

    const patchItems = (ids: string[], to: NetworkLinkStatus) => {
        const set = new Set(ids);
        setItems((xs) => xs.map((x) => (set.has(x._id) ? { ...x, status: to } : x)));
    };

    const decide = useCallback(
        async (index: number, to: NetworkLinkStatus) => {
            const { items: xs, pending: busy, status: filter } = live.current;
            const item = xs[index];
            if (!item || busy.includes(item._id) || inflight.current.has(item._id) || item.status === to) return;
            inflight.current.add(item._id);
            const from = item.status;
            const alsoRejected =
                to === 'confirmed' && item.retailer
                    ? xs
                          .filter((o) => o._id !== item._id && o.status === 'suggested' && o.vendor?._id === item.vendor?._id && o.retailer?.productUuid === item.retailer.productUuid)
                          .map((o) => o._id)
                    : [];
            const decision: Decision = { id: item._id, from, to, alsoRejected };

            // Show it now; the API catches up.
            patchItems([item._id], to);
            if (alsoRejected.length) patchItems(alsoRejected, 'rejected');
            setHistory((h) => [...h, decision]);
            setPending((p) => [...p, item._id]);
            setDecisionError(null);
            const afterDecision = xs.map((x) => (x._id === item._id ? { ...x, status: to } : alsoRejected.includes(x._id) ? { ...x, status: 'rejected' as NetworkLinkStatus } : x));
            setFocus(filter === 'suggested' ? nextOpenIndex(afterDecision, index, (x) => x.status === 'suggested') : Math.min(index + 1, xs.length - 1));

            try {
                const res = await api.setLinkStatus(item._id, to);
                if (!res?.link) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
                setItems((ys) => ys.map((y) => (y._id === item._id ? { ...y, status: res.link.status, decidedAt: res.link.decidedAt, decidedBy: res.link.decidedBy } : y)));
            } catch (e) {
                // Roll back exactly what this decision changed.
                patchItems([item._id], from);
                if (alsoRejected.length) patchItems(alsoRejected, 'suggested');
                setHistory((h) => h.filter((d) => d !== decision));
                setFocus(index);
                const message = networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC');
                setDecisionError(t('ADMIN_NETWORK.REVIEW.DECISION_FAILED', { message }));
                toast.error(message);
            } finally {
                inflight.current.delete(item._id);
                setPending((p) => p.filter((x) => x !== item._id));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [api],
    );

    const undo = useCallback(async () => {
        const { history: h, items: xs, pending: busy } = live.current;
        const last = h[h.length - 1];
        if (!last || busy.includes(last.id) || inflight.current.has(last.id)) return;
        inflight.current.add(last.id);
        const index = xs.findIndex((x) => x._id === last.id);
        patchItems([last.id], 'suggested');
        if (last.alsoRejected.length) patchItems(last.alsoRejected, 'suggested');
        setHistory((hs) => hs.slice(0, -1));
        setPending((p) => [...p, last.id]);
        setDecisionError(null);
        if (index >= 0) setFocus(index);
        try {
            const res = await api.setLinkStatus(last.id, 'suggested');
            if (!res?.link) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
        } catch (e) {
            patchItems([last.id], last.to);
            if (last.alsoRejected.length) patchItems(last.alsoRejected, 'rejected');
            setHistory((hs) => [...hs, last]);
            const message = networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC');
            setDecisionError(t('ADMIN_NETWORK.REVIEW.UNDO_FAILED', { message }));
            toast.error(message);
            inflight.current.delete(last.id);
            setPending((p) => p.filter((x) => x !== last.id));
            return;
        }
        // A confirm also rejected the other suggestions for that product (the ones on
        // this page): put them back too. Any that fail stay rejected, and are said so.
        const settled = await Promise.allSettled(last.alsoRejected.map((id) => api.setLinkStatus(id, 'suggested')));
        const failed = last.alsoRejected.filter((_, i) => settled[i].status === 'rejected');
        if (failed.length) {
            patchItems(failed, 'rejected');
            setDecisionError(t('ADMIN_NETWORK.REVIEW.UNDO_PARTIAL', { count: failed.length }));
        }
        inflight.current.delete(last.id);
        setPending((p) => p.filter((x) => x !== last.id));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api]);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (dialogOpen()) return;
            const action = reviewKeyAction(e);
            if (!action) return;
            const { items: xs, focus: f } = live.current;
            if (!xs.length && action !== 'undo') return;
            e.preventDefault();
            if (action === 'next') setFocus(Math.min(f + 1, xs.length - 1));
            else if (action === 'prev') setFocus(Math.max(f - 1, 0));
            else if (action === 'confirm') decide(f, 'confirmed');
            else if (action === 'reject') decide(f, 'rejected');
            else if (action === 'undo') undo();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [decide, undo]);

    const decidedHere = items.filter((x) => x.status !== status).length;
    const allDecided = status === 'suggested' && items.length > 0 && decidedHere === items.length;

    // After decisions the server's list has moved up under this page: "next"
    // shows the same page again, which now holds the following links.
    const goPage = (p: number) => {
        if (status === 'suggested' && decidedHere > 0 && p > page) setReloadKey((k) => k + 1);
        else setPage(p);
    };

    const collectBulk = async () => {
        const min = percentToScore(bulkPct);
        if (min === undefined) return;
        setBulkCollecting(true);
        setBulkError(null);
        setBulkResult(null);
        try {
            const ids: string[] = [];
            for (let p = 1; ids.length < BULK_CAP; p++) {
                const res = await api.links({ ...query, status: 'suggested', minScore: Math.max(min, query.minScore ?? 0), page: p, limit: BULK_FETCH });
                ids.push(...res.items.map((x) => x._id));
                if (!res.items.length || ids.length >= res.total) break;
            }
            setBulkIds(ids.slice(0, BULK_CAP));
        } catch (e) {
            setBulkError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
        } finally {
            setBulkCollecting(false);
        }
    };

    const runBulk = async () => {
        if (!bulkIds?.length) return;
        setBulkBusy(true);
        setBulkError(null);
        const sum: BulkLinkResult & { requested: number } = { updated: 0, skipped: 0, conflicts: [], requested: bulkIds.length };
        try {
            for (const ids of chunk(bulkIds, NETWORK_BULK_MAX)) {
                const res = await api.bulkLinks(ids, 'confirmed');
                if (!res) throw new Error(t('ADMIN_NETWORK.ERRORS.NO_RESPONSE'));
                sum.updated += res.updated || 0;
                sum.skipped += res.skipped || 0;
                sum.conflicts.push(...(res.conflicts || []));
            }
            setBulkResult(sum);
            setBulkIds(null);
            setReloadKey((k) => k + 1);
        } catch (e) {
            // What did go through is still reported, beside the failure.
            if (sum.updated || sum.skipped || sum.conflicts.length) setBulkResult(sum);
            setBulkError(networkErrorMessage(e) || t('ADMIN_NETWORK.ERRORS.GENERIC'));
            setBulkIds(null);
            setReloadKey((k) => k + 1);
        } finally {
            setBulkBusy(false);
        }
    };

    const filtered = !!(method || vendorId || brandId || minScore !== undefined || q);
    const lastDecision = history[history.length - 1];

    return (
        <AdminPage
            title={t('ADMIN_NETWORK.NAV_REVIEW')}
            parents={[{ label: t('ADMIN_SHELL.NAV.SECTION_CATALOG') }, { label: t('ADMIN_NETWORK.NAV'), href: '/network' }]}
            description={t('ADMIN_NETWORK.REVIEW.DESCRIPTION')}
        >
          {/* `contents`: a hook for tests that leaves the page's own layout alone. */}
          <div className="contents" data-testid="network-review">
            <div className="flex flex-col gap-3" data-testid="review-filters">
                <PillTabs<NetworkLinkStatus>
                    value={status}
                    onChange={(v) => setFilters({ status: v })}
                    ariaLabel={t('ADMIN_NETWORK.FIELDS.STATUS')}
                    options={NETWORK_LINK_STATUSES.map((s) => ({ value: s, label: t(`ADMIN_NETWORK.LINK_STATUS.${vocabKey(s)}`), count: s === status ? total : undefined }))}
                />
                <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                    <NativeSelect className="w-full sm:w-44" value={method} onChange={(e) => setFilters({ method: e.target.value as NetworkLinkMethod | '' })} aria-label={t('ADMIN_NETWORK.FIELDS.METHOD')} data-testid="filter-method">
                        <NativeSelectOption value="">{t('ADMIN_NETWORK.REVIEW.ALL_METHODS')}</NativeSelectOption>
                        {NETWORK_LINK_METHODS.map((m) => (
                            <NativeSelectOption key={m} value={m}>{methodLabel(t, m)}</NativeSelectOption>
                        ))}
                    </NativeSelect>
                    <NativeSelect className="w-full sm:w-56" value={vendorId} onChange={(e) => setFilters({ vendorId: e.target.value })} aria-label={t('ADMIN_NETWORK.FIELDS.RETAILER')} data-testid="filter-retailer">
                        <NativeSelectOption value="">{t('ADMIN_NETWORK.ACTIONS.ALL_RETAILERS')}</NativeSelectOption>
                        {retailers.map((r) => (
                            <NativeSelectOption key={r.vendorId} value={r.vendorId}>{r.name}</NativeSelectOption>
                        ))}
                    </NativeSelect>
                    <NativeSelect className="w-full sm:w-56" value={brandId} onChange={(e) => setFilters({ brandId: e.target.value })} aria-label={t('ADMIN_NETWORK.FIELDS.BRAND')} data-testid="filter-brand">
                        <NativeSelectOption value="">{t('ADMIN_NETWORK.REVIEW.ALL_BRANDS')}</NativeSelectOption>
                        {brands.map((b) => (
                            <NativeSelectOption key={b._id} value={b._id}>{b.name}</NativeSelectOption>
                        ))}
                    </NativeSelect>
                    <div className="flex items-center gap-2">
                        <Label htmlFor="review-min" className="text-xs whitespace-nowrap text-muted-foreground">{t('ADMIN_NETWORK.REVIEW.MIN_SCORE')}</Label>
                        <Input id="review-min" inputMode="numeric" placeholder="0" value={minDraft} onChange={(e) => setMinDraft(e.target.value.replace(/[^0-9.,]/g, ''))} className="h-8 w-20 tabular-nums" data-testid="filter-min" />
                    </div>
                    <SearchField value={search} onChange={setSearch} placeholder={t('ADMIN_NETWORK.REVIEW.SEARCH')} className="sm:w-60" />
                    {filtered && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setParams(writeReviewFilters({ status, method: '', vendorId: '', brandId: '', minScore: undefined, q: '', page: 1 }), { replace: true })}
                        >
                            {t('ADMIN_NETWORK.REVIEW.CLEAR_FILTERS')}
                        </Button>
                    )}
                </div>
            </div>

            {status === 'suggested' && (
                <SectionCard testId="bulk-confirm">
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                            <div className="flex items-center gap-2">
                                <Label htmlFor="bulk-min" className="text-sm whitespace-nowrap">{t('ADMIN_NETWORK.REVIEW.BULK_LABEL')}</Label>
                                <Input id="bulk-min" inputMode="numeric" value={bulkPct} onChange={(e) => setBulkPct(e.target.value.replace(/[^0-9.,]/g, ''))} className="h-8 w-20 tabular-nums" data-testid="bulk-min" />
                            </div>
                            <Button variant="outline" onClick={collectBulk} disabled={bulkCollecting || bulkBusy || percentToScore(bulkPct) === undefined} data-testid="bulk-open">
                                <CheckCheck />
                                {bulkCollecting ? t('ADMIN_NETWORK.REVIEW.BULK_COUNTING') : t('ADMIN_NETWORK.REVIEW.BULK_BUTTON', { score: bulkPct || '—' })}
                            </Button>
                            <p className="text-xs text-muted-foreground sm:ms-2">{t('ADMIN_NETWORK.REVIEW.BULK_HINT')}</p>
                        </div>
                        {bulkError && <ErrorNote message={bulkError} />}
                        {bulkResult && (
                            <div className="flex flex-col gap-1 text-sm" data-testid="bulk-result">
                                <p>{t('ADMIN_NETWORK.REVIEW.BULK_RESULT', { updated: bulkResult.updated, requested: bulkResult.requested, skipped: bulkResult.skipped, conflicts: bulkResult.conflicts.length })}</p>
                                {bulkResult.conflicts.length > 0 && (
                                    <ul className="text-xs text-muted-foreground">
                                        {bulkResult.conflicts.slice(0, 5).map((c) => (
                                            <li key={c.id}>
                                                <span dir="ltr">{c.id}</span> — {c.reason}
                                            </li>
                                        ))}
                                        {bulkResult.conflicts.length > 5 && <li>{t('ADMIN_NETWORK.REVIEW.BULK_MORE_CONFLICTS', { count: bulkResult.conflicts.length - 5 })}</li>}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </SectionCard>
            )}

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground" data-testid="shortcut-legend">
                <span className="inline-flex items-center gap-1"><Kbd>J</Kbd><Kbd>↓</Kbd> {t('ADMIN_NETWORK.REVIEW.KEY_NEXT')}</span>
                <span className="inline-flex items-center gap-1"><Kbd>K</Kbd><Kbd>↑</Kbd> {t('ADMIN_NETWORK.REVIEW.KEY_PREV')}</span>
                <span className="inline-flex items-center gap-1"><Kbd>C</Kbd><Kbd>Enter</Kbd> {t('ADMIN_NETWORK.REVIEW.KEY_CONFIRM')}</span>
                <span className="inline-flex items-center gap-1"><Kbd>X</Kbd><Kbd>⌫</Kbd> {t('ADMIN_NETWORK.REVIEW.KEY_REJECT')}</span>
                <span className="inline-flex items-center gap-1"><Kbd>U</Kbd> {t('ADMIN_NETWORK.REVIEW.KEY_UNDO')}</span>
                {!loading && items.length > 0 && (
                    <span className="ms-auto tabular-nums">{t('ADMIN_NETWORK.REVIEW.POSITION', { current: focus + 1 + page * PAGE_SIZE, total: formatNumber(total) })}</span>
                )}
            </div>

            {decisionError && <ErrorNote message={decisionError} />}

            {loading && !items.length ? (
                <div className="flex flex-col gap-4" data-testid="review-loading">
                    {[0, 1].map((i) => <Skeleton key={i} className="h-[30rem] w-full rounded-xl" />)}
                </div>
            ) : loadError ? (
                <EmptyState
                    title={t('ADMIN_SHELL.COMMON.LOAD_FAILED')}
                    description={loadError}
                    action={<Button variant="outline" onClick={load}>{t('ADMIN_SHELL.COMMON.RETRY')}</Button>}
                    className="py-20"
                />
            ) : !items.length ? (
                <div data-testid="review-empty">
                    {filtered || status !== 'suggested' ? (
                        <EmptyState icon={<SearchX />} title={t('ADMIN_NETWORK.REVIEW.EMPTY_FILTERED_TITLE')} description={t('ADMIN_NETWORK.REVIEW.EMPTY_FILTERED_BODY')} className="py-20" />
                    ) : (
                        <EmptyState icon={<Inbox />} title={t('ADMIN_NETWORK.REVIEW.EMPTY_TITLE')} description={t('ADMIN_NETWORK.REVIEW.EMPTY_BODY')} className="py-20" />
                    )}
                </div>
            ) : (
                <>
                    <div className="flex flex-col gap-4" data-testid="review-queue">
                        {items.map((link, i) => (
                            <ReviewCard
                                key={link._id}
                                ref={(el) => {
                                    cardRefs.current[link._id] = el;
                                }}
                                link={link}
                                focused={i === focus}
                                pending={pending.includes(link._id)}
                                thresholds={thresholds}
                                undoable={lastDecision?.id === link._id}
                                onFocus={() => setFocus(i)}
                                onDecide={(to) => {
                                    setFocus(i);
                                    decide(i, to);
                                }}
                                onUndo={undo}
                            />
                        ))}
                    </div>
                    {allDecided && (
                        <div className="flex flex-col items-center gap-2 py-4 text-center" data-testid="page-done">
                            <p className="text-sm text-muted-foreground">{t('ADMIN_NETWORK.REVIEW.PAGE_DONE', { count: items.length })}</p>
                            <Button variant="outline" onClick={() => (page === 0 ? setReloadKey((k) => k + 1) : setPage(0))} data-testid="load-next">
                                {t('ADMIN_NETWORK.REVIEW.LOAD_NEXT')}
                            </Button>
                        </div>
                    )}
                    <TablePagination className="px-0" page={page} pageSize={PAGE_SIZE} total={total} onPageChange={goPage} />
                </>
            )}

            <ConfirmActionDialog
                open={!!bulkIds}
                onOpenChange={(o) => !o && setBulkIds(null)}
                title={t('ADMIN_NETWORK.REVIEW.BULK_TITLE', { count: bulkIds?.length ?? 0, score: bulkPct })}
                description={bulkIds?.length ? t('ADMIN_NETWORK.REVIEW.BULK_BODY') : t('ADMIN_NETWORK.REVIEW.BULK_NONE')}
                confirmLabel={t('ADMIN_NETWORK.REVIEW.BULK_CONFIRM', { count: bulkIds?.length ?? 0 })}
                confirmDisabled={!bulkIds?.length}
                busy={bulkBusy}
                onConfirm={runBulk}
            >
                {bulkIds && bulkIds.length >= BULK_CAP && <p className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.REVIEW.BULK_CAPPED', { cap: formatNumber(BULK_CAP) })}</p>}
                {filtered && <p className="text-xs text-muted-foreground">{t('ADMIN_NETWORK.REVIEW.BULK_FILTERED')}</p>}
            </ConfirmActionDialog>
          </div>
        </AdminPage>
    );
}

export default NetworkReviewPage;
